import { useCallback, useEffect, useState } from "react";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import Button from "../../common/elements/Button";
import { serverErrMsg } from "../utils/validators";

const toDatetimeLocal = (dt) => {
    if (!dt) return '';
    try { return new Date(dt).toISOString().slice(0, 16); } catch { return ''; }
};

const fromDatetimeLocal = (s) => s ? new Date(s).toISOString() : null;

const Field = ({ label, children }) => (
    <div className="field">
        <label className="label is-small">{label}</label>
        <div className="control">{children}</div>
    </div>
);

const ScheduleTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [detailsState, setDetailsState] = useState(buildLoadingState(null));
    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState({ startDate: '', endDate: '' });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const fetchDetails = useCallback(() => {
        setDetailsState(buildLoadingState(null));
        execute('GET', thor_api_urls.experiment(experimentKey))
            .then(data => {
                const detail = data?.experiment ?? data;
                setDetailsState(buildDataState(detail));
                const sched = detail?.schedule ?? detail?.experimentSchedule ?? {};
                setForm({
                    startDate: toDatetimeLocal(sched.startDate ?? detail?.startDate),
                    endDate:   toDatetimeLocal(sched.endDate   ?? detail?.endDate),
                });
            })
            .catch(err => {
                console.error("Failed to fetch schedule:", err);
                setDetailsState(buildErrorState(err, null));
            });
    }, [execute, experimentKey]);

    useEffect(() => { fetchDetails(); }, [fetchDetails]);

    const isInvalid = form.startDate && form.endDate && new Date(form.endDate) <= new Date(form.startDate);

    const handleSave = () => {
        if (isInvalid) return;
        setIsSaving(true);
        execute('POST', thor_api_urls.scheduleExperiment(experimentKey), {
            startDate: fromDatetimeLocal(form.startDate),
            endDate:   fromDatetimeLocal(form.endDate),
        })
            .then(() => { addToast('Schedule saved.', 'success'); fetchDetails(); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to save schedule.'), 'danger'))
            .finally(() => setIsSaving(false));
    };

    return (
        <StateHandler showLoader={detailsState.loading} showError={detailsState.error}
                      loadingScreen={<SmallLoadingSpinner />}>
            <div className="level mb-4">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">
                        Production Schedule<HelpTooltip text={HELP.schedule.general} />
                    </p>
                </div>
                {editMode && (
                    <div className="level-right">
                        <Button className="is-link is-small" onClick={handleSave} disabled={isSaving || !!isInvalid}>
                            {isSaving ? 'Saving…' : 'Save Schedule'}
                        </Button>
                    </div>
                )}
            </div>

            <div className="columns">
                <div className="column is-half">
                    <Field label={<>Start Date<HelpTooltip text={HELP.schedule.startDate} /></>}>
                        <input className="input is-small" type="datetime-local"
                               value={form.startDate} disabled={!editMode}
                               onChange={e => set('startDate', e.target.value)} />
                        <p className="help has-text-grey">The experiment will be enabled at midnight UTC at the start of this date.</p>
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label={<>End Date<HelpTooltip text={HELP.schedule.endDate} /></>}>
                        <input className="input is-small" type="datetime-local"
                               value={form.endDate} disabled={!editMode}
                               onChange={e => set('endDate', e.target.value)} />
                        <p className="help has-text-grey">The experiment will be stopped at midnight UTC at the end of this date.</p>
                    </Field>
                </div>
            </div>

            {isInvalid && (
                <div className="notification is-danger is-light mt-2">
                    End date must be after start date. Please fix before saving.
                </div>
            )}
        </StateHandler>
    );
};

export default ScheduleTab;
